/**
 * Sheet — the §7.11 drawer, on `@radix-ui/react-dialog` (mission 6 WP03 T021;
 * FR-005, FR-027; DM-01M3C4XR0TYMTDR6BNEBZBQGXF).
 *
 * Right-anchored below the top bar, `--drawer-width`, radius 0, `--shadow-lg`,
 * 1px navy-200 left border. The scrim covers the content area only (below the
 * top bar, right of the rail), so the table and its selected row stay visible.
 * Enters with a 16px slide over `--duration-base`; none under reduced motion
 * (§4.5, P21). Radix traps focus, closes on Esc and restores focus to the
 * trigger.
 *
 * The primitive holds no copy: callers build the close control with
 * `<SheetClose asChild>` and their own `aria-label`. `overlayProps` is spread on
 * the scrim (WP08 sets `data-drawer-overlay`, P20). With no caller
 * `aria-describedby`, none is set (the drawer's title names it).
 */
import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'

import { cn } from './cn'

export const Sheet = DialogPrimitive.Root
export const SheetTrigger = DialogPrimitive.Trigger
export const SheetClose = DialogPrimitive.Close

export type SheetOverlayProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  [data: `data-${string}`]: string | boolean | undefined
}

export interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  overlayProps?: SheetOverlayProps
}

export const SheetContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, SheetContentProps>(
  ({ className, overlayProps, 'aria-describedby': describedBy, children, ...props }, ref) => {
    const { className: overlayClassName, ...overlayRest } = overlayProps ?? {}
    return (
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed bottom-0 left-[var(--rail-width)] right-0 top-[var(--topbar-height)] z-40 bg-[var(--overlay)]',
            overlayClassName,
          )}
          {...overlayRest}
        />
        <DialogPrimitive.Content
          ref={ref}
          aria-describedby={describedBy}
          className={cn(
            'fixed bottom-0 right-0 top-[var(--topbar-height)] z-50 flex w-[var(--drawer-width)] flex-col overflow-y-auto rounded-none border-l border-navy-200 bg-white [box-shadow:var(--shadow-lg)] focus:outline-none',
            'animate-[trellis-drawer-in_var(--duration-base)_var(--ease-out)] motion-reduce:animate-none',
            className,
          )}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    )
  },
)
SheetContent.displayName = 'SheetContent'

export function SheetHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-sheet-header=""
      className={cn('flex h-16 shrink-0 items-center gap-3 border-b border-navy-200 px-6', className)}
      {...props}
    />
  )
}

/** The drawer title, an `h3` (§7.11). */
export const SheetTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Title asChild>
      <h3 ref={ref} className={cn('min-w-0 flex-1 truncate text-h3 text-navy-950', className)} {...props} />
    </DialogPrimitive.Title>
  ),
)
SheetTitle.displayName = 'SheetTitle'

export function SheetBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div data-sheet-body="" className={cn('flex-1 p-6 text-body', className)} {...props} />
}

export function SheetFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-sheet-footer=""
      className={cn(
        'sticky bottom-0 flex h-16 shrink-0 items-center justify-end gap-3 border-t border-navy-200 bg-white px-6',
        className,
      )}
      {...props}
    />
  )
}
